import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import Bugsnag from "@bugsnag/js";

import { addToast } from "../../../Store/Actions";
import FlexBox from "../FlexBox";
import AiChatMessageItem from "./AiChatMessageItem";
import AiChatThinkingBubble from "./AiChatThinkingBubble";
import { getMessageCopyText } from "./aiChatHelpers";
import { updateMessageById } from "../../../Store/Actions/aiChatActions";

const ChatList = styled(FlexBox)`
  width: 100%;
  flex-direction: column;
  row-gap: 1rem;
`;

const AiChatMessageList = ({
  messages = [],
  onCopy,
  onRegenerate,
  onLike,
  onDislike,
  onOpenReportSheet,
  isSubmittingReport: _isSubmittingReport = false,
  reportSheetContext: _reportSheetContext = null,
  thinking = false,
  aiResponding: _aiResponding = false,
  completedTyping,
  setCompletedTyping,
  displayed,
  setDisplayed,
  displayedLengthRef,
  intervalRef,
}) => {
  const dispatch = useDispatch();
  const [openMenuId, setOpenMenuId] = useState(null);

  const menuRefs = useRef({});

  const safeMessages = useMemo(
    () => (Array.isArray(messages) ? messages : []),
    [messages]
  );

  useEffect(() => {
    if (!openMenuId) return;

    const handleOutsideClick = event => {
      const activeMenuRef = menuRefs.current[openMenuId];

      if (!activeMenuRef || !activeMenuRef.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openMenuId]);

  const handleToggleMenu = messageId => {
    setOpenMenuId(previousMenuId =>
      previousMenuId === messageId ? null : messageId
    );
  };

  const handleReport = (message, messageId) => {
    onOpenReportSheet?.({
      type: "message",
      message,
      messageId,
    });
    setOpenMenuId(null);
  };

  const markTypingComplete = messageId => {
    setCompletedTyping(previousState => ({
      ...previousState,
      [messageId]: true,
    }));

    dispatch(
      updateMessageById(messageId, () => ({
        isStreaming: false,
      }))
    );
  };

  const handleCopy = async (message, messageId) => {
    try {
      if (!navigator?.clipboard?.writeText) return;

      await navigator.clipboard.writeText(getMessageCopyText(message));

      dispatch(
        addToast({
          msg: "Copied to clipboard!",
          appearance: "info",
          showBottomToastOnMobile: true,
        })
      );

      onCopy?.(message, messageId);
    } catch {
      Bugsnag.notify(new Error("Failed to copy AI message to clipboard"));
    }
  };

  return (
    <ChatList>
      {safeMessages.map((message, index) => (
        <AiChatMessageItem
          key={message?.id || index}
          message={message}
          index={index}
          messages={safeMessages}
          openMenuId={openMenuId}
          completedTyping={completedTyping}
          menuRefs={menuRefs}
          displayed={displayed}
          setDisplayed={setDisplayed}
          displayedLengthRef={displayedLengthRef}
          intervalRef={intervalRef}
          onCopy={handleCopy}
          onRegenerate={onRegenerate}
          onLike={onLike}
          onDislike={onDislike}
          onToggleMenu={handleToggleMenu}
          onReport={handleReport}
          onTypingComplete={markTypingComplete}
        />
      ))}

      {thinking && <AiChatThinkingBubble />}
    </ChatList>
  );
};

export { AiChatMessageBubble } from "./AiChatMessageBubble";
export default AiChatMessageList;
